import { useLayoutEffect, useState, type RefObject } from 'react';

/** Room kept below for the floating dock (main's pb-28). */
const DOCK_RESERVE_PX = 112;

/**
 * Height that takes an element from where it starts down to the bottom of the
 * window, less room for the dock. Used by the chart frames and the dashboard.
 *
 * Measured rather than written as calc(100dvh - Xrem), because what sits
 * above differs per page — toolbars, error banners, the feed banner — and
 * changes while the page is open. Re-measured on window resize and whenever
 * the page reflows.
 *
 * @param minHeight never shorter than this, however little window is left.
 */
export function useFillHeight(
  ref: RefObject<HTMLElement | null>,
  enabled = true,
  minHeight = 320,
  /** Extra room to keep free below, for content placed under the element. */
  belowPx = 0,
): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      // Distance from the top of the DOCUMENT, so scrolling does not change it.
      const top = el.getBoundingClientRect().top + window.scrollY;
      const next = Math.max(minHeight, Math.round(window.innerHeight - top - DOCK_RESERVE_PX - belowPx));
      setHeight((prev) => (prev === next ? prev : next));
    };

    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    const main = el.closest('main');
    if (main) ro.observe(main);
    // The page as a whole too: the feed banner opening in the sticky header
    // pushes things down without resizing anything inside <main>.
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [ref, enabled, minHeight, belowPx]);

  return height;
}
